import React from "react";

const ClientPreview = ({ onClose, client }) => {
  if (!client) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const Section = ({ title, children }) => (
    <div className="mb-5">
      <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-2 px-1">
        {title}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
        {children}
      </div>
    </div>
  );

  const Field = ({ label, value, isLink = false, isEmail = false }) => {
    const display = value || null;
    return (
      <div className="flex flex-col bg-gray-50 hover:bg-gray-100 p-3 rounded-md transition">
        <span className="text-xs font-medium text-gray-500 mb-0.5">{label}</span>
        {display ? (
          isLink ? (
            <a
              href={display.startsWith("http") ? display : `https://${display}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline text-sm break-all"
            >
              {display}
            </a>
          ) : isEmail ? (
            <a
              href={`mailto:${display}`}
              className="text-blue-600 hover:underline text-sm break-all"
            >
              {display}
            </a>
          ) : (
            <span className="text-gray-900 text-sm break-all">{display}</span>
          )
        ) : (
          <span className="text-gray-400 italic text-sm">—</span>
        )}
      </div>
    );
  };

  const BoolField = ({ label, value }) => (
    <div className="flex flex-col bg-gray-50 hover:bg-gray-100 p-3 rounded-md transition">
      <span className="text-xs font-medium text-gray-500 mb-0.5">{label}</span>
      <span
        className={`text-sm font-semibold ${
          value ? "text-red-500" : "text-green-600"
        }`}
      >
        {value ? "Yes" : "No"}
      </span>
    </div>
  );

  const DocLink = ({ label, url }) =>
    url ? (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-medium rounded-md transition"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        {label}
      </a>
    ) : null;

  const addressStr = (addr) => {
    if (!addr) return null;
    return [addr.street, addr.city, addr.state, addr.country, addr.zipCode]
      .filter(Boolean)
      .join(", ");
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="bg-white shadow-2xl w-full max-w-lg h-full flex flex-col animate-slideInRight">

        {/* Header */}
        <div className="flex justify-between items-center px-5 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            {client.clientCompanyLogoUrl && (
              <img
                src={client.clientCompanyLogoUrl}
                alt="logo"
                className="w-7 h-7 rounded object-contain border border-gray-200"
              />
            )}
            <div>
              <h2 className="text-base font-semibold text-gray-900 leading-tight">
                {client.companyName || "Client Details"}
              </h2>
              {client.role && (
                <span className="text-[10px] text-indigo-600 font-semibold uppercase tracking-wider">
                  {client.role}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition text-lg"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1">

          {/* Company Info */}
          <Section title="Company Info">
            <Field label="Company Name"     value={client.companyName} />
            <Field label="Website"          value={client.websiteUrl} isLink />
            <Field label="LinkedIn"         value={client.linkedInUrl} isLink />
            <Field label="Industry"         value={client.industry} />
            <Field label="Company Stage"    value={client.companyStage} />
            <Field label="Legal Type"       value={client.legalType} />
            <Field label="Employee Count"   value={client.employeeCount} />
            <Field label="Annual Revenue"   value={client.annualRevenue} />
          </Section>

          {/* CRM */}
          <Section title="CRM">
            <Field label="Relationship Type" value={client.relationshipType} />
            <Field label="Lead Status"        value={client.leadStatus} />
            <Field label="Lead Source"        value={client.leadSource} />
            <Field label="Contact Owner"      value={client.contactOwner} />
            <Field label="Created By"         value={client.createdBy} />
          </Section>

          {/* Primary Contact */}
          <Section title="Primary Contact">
            <Field label="Name"      value={client.primaryContact} />
            <Field label="Job Title" value={client.jobTitle} />
            <Field label="Email"     value={client.email} isEmail />
            <Field label="Phone"     value={client.phone} />
          </Section>

          {/* POC / Hiring Manager */}
          <Section title="Point of Contact">
            <Field label="Hiring Manager" value={client.hiringManager} />
            <Field label="POC Name"       value={client.pocName} />
            <Field label="POC Job Title"  value={client.pocJobTitle} />
            <Field label="POC Email"      value={client.pocEmail} isEmail />
            <Field label="POC Phone"      value={client.pocPhone} />
          </Section>

          {/* Addresses */}
          <Section title="Addresses">
            <div className="sm:col-span-2">
              <Field label="Billing Address"  value={addressStr(client.billingAddress)} />
            </div>
            {!client.sameAsBilling && (
              <div className="sm:col-span-2">
                <Field label="Shipping Address" value={addressStr(client.shippingAddress)} />
              </div>
            )}
            {client.sameAsBilling && (
              <div className="sm:col-span-2 text-xs text-gray-400 italic px-1">
                Shipping same as billing
              </div>
            )}
          </Section>

          {/* Tax & Payment */}
          <Section title="Tax & Payment">
            <Field label="Tax ID Type"   value={client.taxIdType} />
            <Field label="Tax ID Number" value={client.taxIdNumber} />
            <Field label="Tax Status"    value={client.taxStatus} />
            <Field label="Payment Terms" value={client.paymentTerms} />
            <Field label="Currency"      value={client.currency} />
          </Section>

          {/* Documents */}
          {(client.primaryAgreementUrl ||
            client.workOrderUrl ||
            client.supportingDocumentUrls?.length > 0) && (
            <div className="mb-5">
              <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-2 px-1">
                Documents
              </h3>
              <div className="flex flex-wrap gap-2">
                <DocLink label="Primary Agreement" url={client.primaryAgreementUrl} />
                <DocLink label="Work Order"        url={client.workOrderUrl} />
                {client.supportingDocumentUrls?.map((url, i) => (
                  <DocLink key={i} label={`Supporting Doc ${i + 1}`} url={url} />
                ))}
              </div>
            </div>
          )}

          {/* Preferences */}
          <Section title="Preferences">
            <BoolField label="Do Not Call"  value={client.doNotCall} />
            <BoolField label="Do Not Email" value={client.doNotEmail} />
          </Section>

          {/* Meta */}
          <Section title="Meta">
            <Field label="Created At"    value={formatDate(client.createdAt)} />
            <Field label="Last Activity" value={formatDate(client.lastActivity)} />
            <Field label="Organization ID" value={client.organizationId} />
          </Section>

        </div>

        {/* Footer */}
        <div className="flex justify-end items-center gap-2 px-5 py-3 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
          >
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        .animate-slideInRight {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ClientPreview;